/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { createSlice } from '@reduxjs/toolkit';
import {
  startOfMonth,
  subMonths,
  endOfMonth,
  previousSunday,
  isSunday,
  isSaturday,
  nextSaturday,
  differenceInCalendarDays,
} from 'date-fns';
import { decode } from 'html-entities';
import { capitalizeFirst } from 'modules/common/helpers/strings';
import { signInActions } from 'modules/sign-in/slice';
import { DEMO_ROLE } from 'modules/common/constants/roles';
import {
  BOOKING_DATE,
  TAGS,
  REPORT_TYPE,
  KEYS,
  SLICE,
  CURRENCY_TYPES,
  WORKBOOK_DATE_SELECTION,
  MODEL_OPTIONS,
  EXPORT_OPTIONS,
} from 'modules/dashboard/constants';
import { getHotelFocusOnValue } from 'modules/dashboard/functions';
import ERROR_MESSAGE_TYPES from 'modules/common/constants/error-messages';
import { isEmpty } from 'modules/common/helpers/object';
import PAGINATION from 'modules/common/constants/pagination-limits';
import { getCurrencySchema } from './components/tab-container/inner-filter/functions';
//
const stayDate = {
  startDate: new Date().toISOString(),
  endDate: new Date().toISOString(),
  key: 'selection',
};
const trendStayDate = {
  startDate: new Date(startOfMonth(subMonths(new Date(), 1))).toISOString(),
  endDate: new Date(endOfMonth(subMonths(new Date(), 1))).toISOString(),
  key: 'selection',
};
const weeklyTrendStayDate = {
  startDate: new Date().toISOString(),
  endDate: new Date().toISOString(),
  key: 'selection',
};
if (isSunday(new Date(trendStayDate.startDate))) {
  weeklyTrendStayDate.startDate = new Date(trendStayDate.startDate).toISOString();
} else {
  weeklyTrendStayDate.startDate = previousSunday(new Date(trendStayDate.startDate)).toISOString();
}
if (isSaturday(new Date(trendStayDate.endDate))) {
  weeklyTrendStayDate.endDate = new Date(trendStayDate.endDate).toISOString();
} else {
  weeklyTrendStayDate.endDate = nextSaturday(new Date(trendStayDate.endDate)).toISOString();
}
/**
 * Initial states of Dashboard function are defined here
 */
export const initialState = {
  loading: false,
  pingLoading: false,
  isSessionTimedOut: false,
  reportType: REPORT_TYPE.SUMMARY,
  embedTokenDetails: {},
  hotelList: null,
  // latestUpdatedHotelList: {},
  hotelName: null,
  visuals: {},
  visualFilters: {},
  pmsDate: null,
  groupPmsDates: null,
  pmsSync: false,
  breakdown: [],
  isSignedIn: true,
  lastUpdatedDate: null,
  hotelChange: false,
  tabChange: false,
  isDemoUser: false,
  userRole: null,
  tabAccess: {},
  isUserConfigurationNotExist: false,
  isTokenRevoked: false,
  tabValue: 0,
  currentUrl: '',
  commandUrlFilterList: {},
  commandUrlFilterDropdownList: {},
  commandFilterLoader: false,
  landingPage: {},
  utmBreakdown: [],
  //
  targetDate: stayDate,
  summaryPaceHorizon: null,
  summaryPaceVariable: null,
  summaryForecastFilter: true,
  forecastFilters: [],
  //
  paceOtb: 0,
  paceBookingStyle: false,
  paceBookingDate: null,
  //
  trendTargetDate: trendStayDate,
  weeklyTrendStartDate: weeklyTrendStayDate.startDate,
  weeklyTrendEndDate: weeklyTrendStayDate.endDate,
  trendBookingDate: null,
  trendOtb: 0,
  trendBookingStyle: false,
  //
  hotelGroups: {},
  hotelGroupsList: [],
  hotelGroupsDropdownList: [],
  predefinedHotelList: [],
  existedHotelGroup: null,
  hotelGroupName: '',
  description: '',
  hotelGroupQuery: '',
  naturalLanguageText: '',
  enableHotelGroups: false,
  notifications: [],
  mergedHotelList: [],
  favoriteHotels: [],
  nonFavoriteHotels: [],
  assignedHotelGroups: null,
  IsSavedCommandFilterLoaded: false,
  currencyFilter: {},
  localCurrency: CURRENCY_TYPES.USD,
  currencyList: [
    {
      id: CURRENCY_TYPES.USD,
      label: CURRENCY_TYPES.USD,
    },
  ],
  //
  workbookDateType: WORKBOOK_DATE_SELECTION[0],
  isHotelForecast: null,
  hotelForecastDisabledText: 'Forecasts not available',
  isHotelSetModified: false,
  //
  urlTab: null,
  commandRun: null,
  pmsTooltipIsDisplay: false,
  appTheme: '',
  //
  originalHotelName: null,
  originalHotelList: null,
  isDemoModeActivated: false,
  //
  systemInformation: {},
  appName: null,
  isPrimaryHotelSetOverTime: false,
  isPrimaryHotelSetOverTimeDisabled: false,
  isSecondaryHotelSetOverTime: false,
  isSecondaryHotelSetOverTimeDisabled: false,
  invalidPrimaryHotelList: [],
  invalidSecondaryHotelList: [],
  sameStoreComparisonDate: null,
  hotelSetUpdates: {
    primary: { updated: false, name: null },
    secondary: { updated: false, name: null },
  },
  lastPartialBookingDate: null,
  partialHours: null,
  lastFullBookingDate: null,
  maxStayDate: null,
  resetBookingDate: {
    startDate: new Date(BOOKING_DATE).toISOString(),
    endDate: new Date().toISOString(),
    key: 'selection',
  },
  isLeapSelection: false,
  naturalLanguageQueryList: [],
  initializeLLMRequest: false,
  //
  isDataExporting: {},
  imageExport: [],
  errorData: [],
  exportProgress: {},
  interpreterOptions: {
    modelName: MODEL_OPTIONS?.[0]?.value,
    exportType: EXPORT_OPTIONS.DATA_EXPORT,
  },
  // tour
  tourRun: false,
  tourSteps: [],
  tourTarget: '',
  advancedQueryErrorMessage: '',
  tourUrlTail: '',
  //
  openVisualAgentModal: false,
  isHistoricalViOverlayActive: false,
  visualAnalysis: {
    results: [],
    page: 1,
    totalPages: 0,
    totalResults: 0,
},
  viResponseListError: false,
  viResponseListLoading: false,
  isFirstTimeInterpreterAgentUsed: false,
  interpreterAgentClicked: false,
  maxForecastDate: {
    roomsPmsOn: null,
    roomsPmsOff: null,
    revenuePmsOn: null,
    revenuePmsOff: null,
  },
  shareUrlTabList: {},
  tabList: {},
  encodedTabList: {},
};
/**
 * All actions related to dashboard feature are defined here
 */
export const dashboardSlice = createSlice({
  name: SLICE.DASHBOARD,
  initialState,
  reducers: {
    getEmbedToken(state) {
      state.loading = true;
    },
    getEmbedTokenSucceeded(state, action) {
      state.loading = false;
      state.embedTokenDetails = action?.payload;
    },
    getEmbedTokenFailed(state) {
      state.loading = false;
    },
    setHotelName(state, action) {
      state.hotelName = action?.payload;
      state.enableHotelGroups = action?.payload && 'hotels' in action.payload;
    },
    setReportType(state, action) {
      state.reportType = action?.payload;
    },
    getVisuals(state) {
      state.loading = true;
    },
    getVisualsSucceeded(state, action) {
      state.loading = false;
      state.visuals = action?.payload;
    },
    getVisualsFailed(state) {
      state.loading = false;
    },
    getVisualFilters(state) {
      state.loading = true;
    },
    getVisualFiltersSucceeded(state, action) {
      state.loading = false;
      state.visualFilters = action?.payload;
      state.summaryPaceHorizon = action?.payload?.results?.filter(
        (filter) => filter?.tags?.includes(TAGS.PACE_HORIZON) && filter?.isDefault === true
      )[0]?.id;
      state.paceWidgetPaceHorizon = action?.payload?.results?.filter(
        (filter) => filter?.tags?.includes(TAGS.PACE_HORIZON) && filter?.isDefault === true
      )[0]?.id;
      state.summaryPaceVariable = action?.payload?.results?.filter(
        (filter) => filter?.tags?.includes(TAGS.SUMMARY_PACE_VARIABLE) && filter?.isDefault === true
      )[0]?.values?.[0];
      state.currencyFilter = getCurrencySchema(action?.payload?.results);
    },
    getVisualFiltersFailed(state) {
      state.loading = false;
    },
    getHotelList(state) {
      state.loading = true;
    },
    getHotelListSucceeded(state, action) {
      state.loading = false;
      const hotel = getHotelFocusOnValue(
        state.isDemoUser,
        action?.payload,
        state.isDemoModeActivated
      );
      state.hotelList = hotel?.hotelDropdownList;
    },
    getHotelListFailed(state) {
      state.loading = false;
    },
    // getLatestUpdatedHotelList(state) {
    //   state.loading = true;
    // },
    // getLatestUpdatedHotelListSucceeded(state, action) {
    //   state.loading = false;
    //   state.latestUpdatedHotelList = action?.payload;
    // },
    // getLatestUpdatedHotelListFailed(state) {
    //   state.loading = false;
    // },
    setPmsDate(state, action) {
      state.pmsDate = action?.payload;
    },
    setGroupPmsDates(state, action) {
      state.groupPmsDates = action?.payload;
    },
    setPmsSync(state, action) {
      state.pmsSync = action?.payload;
    },
    getBreakdown(state) {
      state.loading = true;
    },
    getBreakdownSucceeded(state, action) {
      state.loading = false;
      state.breakdown = action?.payload?.results;
    },
    getBreakdownFailed(state) {
      state.loading = false;
    },
    pingSqlWareHouse(state) {
      state.pingLoading = true;
    },
    pingSqlWareHouseSucceeded(state) {
      state.pingLoading = false;
    },
    pingSqlWareHouseFailed(state) {
      state.pingLoading = false;
    },
    getSystemInformation(state) {
      state.loading = true;
    },
    getSystemInformationSucceeded(state, action) {
      state.loading = false;
      state.lastUpdatedDate = action?.payload?.results?.find(
        (filter) => filter?.key === KEYS.LAST_UPDATED_DATE
      )?.value;
      state.systemInformation = action?.payload?.results?.find(
        (filter) => filter?.key === 'maintenance'
      )?.value;
      state.lastPartialBookingDate = action?.payload?.results?.find(
        (filter) => filter?.key === KEYS.LAST_BOOKING_DATE_PARTIAL
      )?.value;
      state.partialHours = action?.payload?.results?.find(
        (filter) => filter?.key === KEYS.PARTIAL_HOURS
      )?.value;
      state.lastFullBookingDate = action?.payload?.results?.find(
        (filter) => filter?.key === KEYS.LAST_BOOKING_DATE_24HRS
      )?.value;
      state.maxStayDate = action?.payload?.results?.find(
        (filter) => filter?.key === KEYS.MAX_STAY_DATE
      )?.value;
      state.maxForecastDate = {
        roomsPmsOn: action?.payload?.results?.find(
          (filter) => filter?.key === KEYS.MAX_FORECAST_DATE_ROOMS_PMS_ON
        )?.value,
        roomsPmsOff: action?.payload?.results?.find(
          (filter) => filter?.key === KEYS.MAX_FORECAST_DATE_ROOMS_PMS_OFF
        )?.value,
        revenuePmsOn: action?.payload?.results?.find(
          (filter) => filter?.key === KEYS.MAX_FORECAST_DATE_REV_PMS_ON
        )?.value,
        revenuePmsOff: action?.payload?.results?.find(
          (filter) => filter?.key === KEYS.MAX_FORECAST_DATE_REV_PMS_OFF
        )?.value,
      };
    },
    getSystemInformationFailed(state) {
      state.loading = false;
    },
    setTabChange(state, action) {
      state.tabChange = action.payload;
    },
    setHotelChange(state, action) {
      state.hotelChange = action.payload;
    },
    setCurrentUrl(state, action) {
      state.currentUrl = action.payload;
    },
    setTabValue(state, action) {
      state.tabValue = action.payload;
    },
    setTabAccess(state, action) {
      state.tabAccess = action?.payload;
    },
    setTokenRevocationError(state, action) {
      state.isTokenRevoked = action?.payload;
    },
    setSessionTimeOut(state, action) {
      state.isSessionTimedOut = action?.payload;
    },
    setIsSignedIn(state, action) {
      state.isSignedIn = action?.payload;
    },
    resetUserConfigurationStatus(state) {
      state.isUserConfigurationNotExist = false;
    },
    saveCommandUrlFilter(state) {
      state.loading = true;
    },
    saveCommandUrlSucceeded(state, action) {
      state.loading = false;
      state.commandUrlFilterList = action?.payload;
    },
    saveCommandUrlFailed(state) {
      state.loading = false;
    },
    getCommandFilter(state) {
      state.commandFilterLoader = true;
    },
    getCommandFilterSucceeded(state, action) {
      state.commandFilterLoader = false;
      state.commandUrlFilterList = action?.payload;
    },
    getCommandFilterFailed(state) {
      state.commandFilterLoader = false;
    },
    getCommandFilterDropDownList(state) {
      state.commandFilterLoader = true;
    },
    getCommandFilterDropDownListSucceeded(state, action) {
      state.commandFilterLoader = false;
      state.commandUrlFilterDropdownList = action?.payload;
    },
    getCommandFilterDropDownListFailed(state) {
      state.commandFilterLoader = false;
    },
    deleteCommandFilter(state) {
      state.loading = true;
    },
    deleteCommandFilterSucceeded(state) {
      state.loading = false;
    },
    deleteCommandFilterFailed(state) {
      state.loading = false;
    },
    getScrollCommandFilter(state) {
      state.commandFilterLoader = true;
    },
    getScrollCommandFilterSucceeded(state, action) {
      state.commandFilterLoader = false;
      state.commandUrlFilterList = {
        ...action.payload,
        results: [...state.commandUrlFilterList.results, ...action.payload.results],
      };
    },
    getScrollCommandFilterFailed(state) {
      state.commandFilterLoader = false;
    },
    getUtmBreakdown(state) {
      state.loading = true;
    },
    getUtmBreakdownSucceeded(state, action) {
      state.loading = false;
      state.utmBreakdown = action?.payload?.results;
    },
    getUtmBreakdownFailed(state) {
      state.loading = false;
    },
    //
    setPaceHorizon(state, action) {
      state.summaryPaceHorizon = action?.payload;
    },
    setPaceVariable(state, action) {
      state.loading = false;
      state.summaryPaceVariable = action?.payload;
    },
    setSummaryForecastFilter(state, action) {
      state.summaryForecastFilter = action?.payload;
    },
    setTargetDate(state, action) {
      state.targetDate = action?.payload;
    },
    getForecastFilters(state) {
      state.loading = true;
    },
    getForecastFiltersSucceeded(state, action) {
      state.loading = false;
      state.forecastFilters = action?.payload?.results
        ?.map((item) => ({
          label: capitalizeFirst(item?.label),
          pmsSyncOff: item?.pmsSyncOff,
          variable: item?.variable,
          stayDate: item?.stayDate.split('T')[0] ?? null,
          date: item.label.substring(item.label.indexOf(' ', item.label.indexOf(' ') + 1) + 1),
        }))
        .sort((a, b) => differenceInCalendarDays(new Date(b.date), new Date(a.date)));
    },
    getForecastFiltersFailed(state) {
      state.loading = false;
    },
    setForecastFilters(state, action) {
      state.forecastFilters = action?.payload;
    },
    //
    setTrendBookingDate(state, action) {
      state.trendBookingDate = action?.payload;
    },
    setTrendTargetDate(state, action) {
      state.trendTargetDate = action?.payload;
    },
    setTrendOtb(state, action) {
      state.trendOtb = action?.payload;
    },
    setTrendBookingStyle(state, action) {
      state.trendBookingStyle = action?.payload;
    },
    setWeeklyTrendStartDate(state, action) {
      state.weeklyTrendStartDate = action?.payload;
    },
    setWeeklyTrendEndDate(state, action) {
      state.weeklyTrendEndDate = action?.payload;
    },
    getHotelGroupList(state) {
      state.loading = true;
    },
    getHotelGroupListSucceeded(state, action) {
      state.loading = false;
      state.hotelGroups = action?.payload;
    },
    getHotelGroupListFailed(state, action) {
      state.loading = false;
      if (action?.payload?.message === ERROR_MESSAGE_TYPES.USER_CONFIGURATION_NOT_FOUND) {
        state.isUserConfigurationNotExist = true;
      }
    },
    setHotelGroupDropdownList(state, action) {
      state.hotelGroupsDropdownList = action?.payload;
    },
    setPredefinedHotelList(state, action) {
      state.predefinedHotelList = action?.payload;
    },
    setSelectedHotelGroup(state, action) {
      if (state && state.hotelName && 'hotels' in state.hotelName) {
        if (!isEmpty(state.hotelName) && action?.payload?.hotelGroupDropdownList?.length === 0) {
          state.hotelName = action?.payload?.hotelList[0];
        } else if (state.hotelName && action?.payload?.hotelGroupDropdownList?.length > 0) {
          const newGroup = action?.payload?.hotelGroupDropdownList?.find(
            (a) => a?.id === state.hotelName?.id
          );
          if (newGroup) {
            state.hotelName = newGroup;
          } else {
            state.hotelName = action?.payload?.hotelList[0];
          }
        } else {
          state.hotelName = action?.payload?.hotelList[0];
        }
      }
      state.enableHotelGroups = state && state.hotelName && 'hotels' in state.hotelName;
    },
    getLoadHotelGroupList(state) {
      state.loading = false;
    },
    getLoadHotelGroupListSucceeded(state, action) {
      state.loading = false;
      state.hotelGroupsList = action?.payload;
    },
    getLoadHotelGroupListFailed(state) {
      state.loading = false;
    },
    setHotelSetUpdated(state, action) {
      state.hotelSetUpdates = action.payload;
    },
    createHotelGroup(state) {
      state.loading = true;
    },
    createHotelGroupSucceeded(state, action) {
      state.loading = false;
      state.hotelGroupsList.results = state?.hotelGroupsList?.results?.map((object) => {
        const updatedGroup = action?.payload?.results?.find(
          (updatedObject) =>
            updatedObject?.hotelGroups?.hotelGroupName === object?.hotelGroups?.hotelGroupName
        );
        if (updatedGroup) {
          return updatedGroup;
        }
        return object;
      });
      state.hotelGroups.results = state?.hotelGroups?.results?.map((object) => {
        const updatedGroup = action?.payload?.results?.find(
          (updatedObject) =>
            updatedObject?.hotelGroups?.hotelGroupName === object?.hotelGroups?.hotelGroupName
        );
        if (updatedGroup) {
          return updatedGroup;
        }
        return object;
      });

      // Add new groups if not found in the state
      const existingGroupNames = state.hotelGroups?.results?.map(
        (obj) => obj?.hotelGroups?.hotelGroupName
      );
      action?.payload?.results?.forEach((updatedObject) => {
        if (!existingGroupNames?.includes(updatedObject?.hotelGroups?.hotelGroupName)) {
          state?.hotelGroups?.results?.push(updatedObject);
        }
      });
      state.isHotelSetModified = true;
    },
    createHotelGroupFailed(state) {
      state.loading = false;
    },
    getHotelGroupNameValidation(state) {
      state.loading = true;
    },
    getHotelGroupNameValidationSucceeded(state, action) {
      state.loading = false;
      state.existedHotelGroup = action?.payload;
    },
    getHotelGroupNameValidationFailed(state) {
      state.loading = false;
    },
    resetHotelGroupNameValidationStatus(state) {
      state.existedHotelGroup = null;
    },
    setSelectedHotelGroupName(state, action) {
      state.hotelGroupName = action.payload;
    },
    setSelectedHotelGroupDescription(state, action) {
      state.description = action.payload;
    },
    deleteHotelGroup(state) {
      state.loading = true;
    },
    deleteHotelGroupSucceeded(state, action) {
      state.loading = false;
      state.isHotelSetModified = true;
      const [, hotelGroupName] = action.payload.query.split('=');
      state.hotelGroupsList.results = state?.hotelGroupsList?.results?.filter(
        (object) => object?.hotelGroups?.hotelGroupName !== hotelGroupName
      );
      state.hotelGroups.results = state?.hotelGroups?.results?.filter(
        (object) => object?.hotelGroups?.hotelGroupName !== hotelGroupName
      );
    },
    deleteHotelGroupFailed(state) {
      state.loading = false;
    },
    setHotelGroupQuery(state, action) {
      state.hotelGroupQuery = decode(action.payload);
    },
    setNaturalLanguageQuery(state, action) {
      state.naturalLanguageText = action.payload;
    },
    setEnableHotelGroups(state, action) {
      state.enableHotelGroups = action.payload;
    },
    setLandingPage(state, action) {
      state.landingPage = action.payload;
    },
    resetLandingPage(state) {
      state.landingPage = {};
    },
    getNotificationsList() {},
    getNotificationsListSucceeded(state, action) {
      state.notifications = action.payload.results;
    },
    getNotificationsListFailed() {},
    getProgressNotificationsList() {},
    getProgressNotificationsListSucceeded(state, action) {
      const processData = action.payload.results?.filter(
        (ll) => ll?.payload?.type === 'PROGRESS' && ll.isOpen === false
      );
      if (processData) {
        state.exportProgress = processData.reduce((map, dataObject) => {
          const id = dataObject?.payload?.id;
          if (id) {
            if (!map[id]) {
              map[id] = [];
            }
            map[id] = [...map[id], dataObject];
          }
          return map;
        }, {});
      }
      const imageData = action.payload.results?.filter(
        (ll) => ll?.payload?.type === 'LLM' && ll.isOpen === false
      );
      if (imageData?.length > 0) {
        const isDuplicate = state.visualAnalysis?.results.some(
          (item) =>
            `${item?.response?.title}-${item.response?.requestedTime}` ===
              `${imageData?.[0]?.payload?.response?.response?.title}-${imageData?.[0]?.payload?.response?.response?.requestedTime}` 
        );

        if (!isDuplicate) {
          state.isDataExporting[imageData?.[0]?.payload?.id] = false;
          state.imageExport = imageData;
          state.visualAnalysis.results = [
            imageData?.[0]?.payload?.response,
            ...state.visualAnalysis.results,
          ];
          state.interpreterAgentClicked = false;
          state.openVisualAgentModal = true;
        }
      }
      const errorData = action.payload.results?.filter(
        (ll) => ll?.payload?.type === 'ERROR' && ll.isOpen === false
      );
      if (errorData?.length > 0) {
        state.openVisualAgentModal = true;
        state.isDataExporting[errorData?.[0]?.payload?.id] = false;
        state.errorData = errorData;
        state.interpreterAgentClicked = false;
      }
    },
    getProgressNotificationsListFailed() {},

    updateNotification(state) {
      state.loading = false;
    },
    updateNotificationSucceeded(state, action) {
      state.loading = false;
      state.notifications = state.notifications?.map((notification) => {
        if (notification.id === action.payload.id) {
          return action.payload;
        }
        return notification;
      });
    },
    updateNotificationFailed(state) {
      state.loading = false;
    },
    setMergedHotelList(state, action) {
      state.mergedHotelList = action?.payload;
    },
    setFavoriteHotelList(state, action) {
      state.favoriteHotels = action?.payload;
    },
    setNonFavoriteHotelList(state, action) {
      state.nonFavoriteHotels = action?.payload;
    },
    addHotelToFavorite(state) {
      state.loading = true;
    },
    addHotelToFavoriteSucceeded(state, action) {
      state.loading = false;
      state.hotelList = state?.hotelList?.map((hotel) => ({
        ...hotel,
        isFavorite: action?.payload?.includes(hotel?.id),
      }));
    },
    addHotelToFavoriteFailed(state) {
      state.loading = false;
    },
    addHotelGroupToFavorites(state) {
      state.loading = true;
    },
    addHotelGroupToFavoritesSucceeded(state, action) {
      state.loading = false;
      state.hotelGroupsList.results = state?.hotelGroupsList?.results?.map((object) => {
        const updatedGroup = action?.payload?.results?.find(
          (updatedObject) =>
            updatedObject?.hotelGroups?.hotelGroupName === object?.hotelGroups?.hotelGroupName
        );
        if (updatedGroup) return updatedGroup;
        return object;
      });
      state.hotelGroups.results = state?.hotelGroups?.results?.map((object) => {
        const updatedGroup = action?.payload?.results?.find(
          (updatedObject) =>
            updatedObject?.hotelGroups?.hotelGroupName === object?.hotelGroups?.hotelGroupName
        );
        if (updatedGroup) return updatedGroup;
        return object;
      });
    },
    addHotelGroupToFavoritesFailed(state) {
      state.loading = false;
    },
    getAssignedHotelGroupList(state) {
      state.loading = true;
    },
    getAssignedHotelGroupListSucceeded(state, action) {
      state.loading = false;
      state.assignedHotelGroups = action?.payload;
    },
    getAssignedHotelGroupListFailed(state) {
      state.loading = false;
      state.assignedHotelGroups = [];
    },
    setIsSavedCommandFilterLoaded(state, action) {
      state.IsSavedCommandFilterLoaded = action.payload;
    },
    setCurrencyFilter(state, action) {
      state.currencyFilter = action?.payload;
    },
    setLocalCurrency(state, action) {
      state.localCurrency = action.payload;
    },
    setLocalCurrencyList(state, action) {
      const list = state.currencyList?.filter((currency) => currency.id === CURRENCY_TYPES.USD);
      const newCurrencyObj = {
        id: action.payload,
        label: action.payload,
      };
      //
      const arr = Array.from(new Set([...list, newCurrencyObj]));
      state.currencyList = arr;
    },
    removeOtherLocalCurrenciesFromList(state) {
      state.currencyList = [
        {
          id: CURRENCY_TYPES.USD,
          label: CURRENCY_TYPES.USD,
        },
      ];
      state.localCurrency = CURRENCY_TYPES.USD;
    },
    setWorkbookDateType(state, action) {
      state.workbookDateType = action?.payload;
    },
    setIsHotelForecast(state, action) {
      state.isHotelForecast = action?.payload;
    },
    setHotelForecastDisabledText(state, action) {
      state.hotelForecastDisabledText = action?.payload;
    },
    setIsHotelSetModified(state, action) {
      state.isHotelSetModified = action?.payload;
    },
    setUrlTab(state, action) {
      state.urlTab = action?.payload;
    },
    setCommandRun(state, action) {
      // each execution of a command url gets a new id with the command keys it contains
      state.commandRun = {
        id: (state.commandRun?.id ?? 0) + 1,
        keys: action?.payload ?? [],
        isStayDateApplied: false,
        values: {},
      };
    },
    setCommandRunValue(state, action) {
      // value of a command that every tab applies (pms sync, vi response), kept for the tab of the url
      if (state.commandRun) {
        state.commandRun.values[action.payload.key] = action.payload.value;
      }
    },
    setCommandStayDateApplied(state) {
      // stay date command of the current command url is applied, tab commands can follow
      if (state.commandRun) {
        state.commandRun.isStayDateApplied = true;
      }
    },
    setPmsTooltipIsDisplay(state, action) {
      state.pmsTooltipIsDisplay = action?.payload;
    },
    setAppTheme(state, action) {
      state.appTheme = action.payload;
    },
    setOriginalHotelName: (state, action) => {
      state.originalHotelName = action.payload; // Store only once
    },
    resetHotelName: (state, action) => {
      if (state.originalHotelName) {
        state.hotelName = action?.payload;
        state.enableHotelGroups = action?.payload && 'hotels' in action.payload;
      }
    },
    setOriginalHotelList: (state, action) => {
      if (state.originalHotelList === null) {
        state.originalHotelList = action.payload; // Store only once
      }
    },
    resetHotelList: (state, action) => {
      if (state.originalHotelList !== null) {
        state.hotelList = action?.payload;
      }
    },
    setAppName(state, action) {
      state.appName = action?.payload;
    },
    setIsPrimaryHotelSetOverTime(state, action) {
      state.isPrimaryHotelSetOverTime = action?.payload;
    },
    setIsPrimaryHotelSetOverTimeDisabled(state, action) {
      state.isPrimaryHotelSetOverTimeDisabled = action?.payload;
    },
    setIsSecondaryHotelSetOverTime(state, action) {
      state.isSecondaryHotelSetOverTime = action?.payload;
    },
    setIsSecondaryHotelSetOverTimeDisabled(state, action) {
      state.isSecondaryHotelSetOverTimeDisabled = action?.payload;
    },
    setInvalidSecondaryHotelList(state, action) {
      state.invalidSecondaryHotelList = action?.payload;
    },
    setInvalidPrimaryHotelList(state, action) {
      state.invalidPrimaryHotelList = action?.payload;
    },
    setSameStoreComparisonDate(state, action) {
      state.sameStoreComparisonDate = action?.payload;
    },
    getAdvancedQueryLLM(state) {
      state.loading = true;
      state.initializeLLMRequest = true;
    },
    getAdvancedQueryLLMInSucceeded(state, action) {
      state.loading = false;
      state.initializeLLMRequest = false;
      state.hotelGroupQuery =
        action.payload?.openAIResponse?.output?.queryLanguageQuery === 'NA'
          ? 'No Result'
          : action.payload?.openAIResponse?.output?.queryLanguageQuery;
      const option = {
        label: action.payload?.naturalLanguageQuery.naturalLanguageText,
        id: action.payload?.naturalLanguageQuery.id,
      };
      if (state.naturalLanguageQueryList < PAGINATION.NATURAL_LANGUAGE_QUERY_LIST_PER_PAGE) {
        state.naturalLanguageQueryList.push(option);
      } else {
        state.naturalLanguageQueryList[0] = option;
      }
    },
    getAdvancedQueryLLMInFailed(state, action) {
      state.advancedQueryErrorMessage = action.payload.message;
      state.loading = false;
      state.initializeLLMRequest = false;
    },
    setRestLLMRequestState(state, action) {
      state.initializeLLMRequest = action.payload;
    },
    getNaturalLanguageQuery(state) {
      state.loading = true;
    },
    getNaturalLanguageQuerySucceeded(state, action) {
      state.naturalLanguageQueryList = action.payload?.results?.map((result) => ({
        id: result.id,
        label: result.naturalLanguageText,
      }));
      state.loading = false;
    },
    getNaturalLanguageQueryFailed(state) {
      state.loading = false;
    },
    setAdvancedQueryLLMOut(state) {
      state.hotelGroupQuery = '';
    },
    setResetBookingDate(state, action) {
      state.resetBookingDate = action?.payload;
    },
    setIsLeapSelection(state, action) {
      state.isLeapSelection = action?.payload;
    },
    exportVisualImage(state, action) {
      state.isDataExporting[action.payload.dataExport.id] = true;
    },
    exportVisualImageSucceeded() {},
    exportVisualImageFailed() {},
    exportVisualData(state, action) {
      state.isDataExporting[action.payload.id] = true;
    },
    exportVisualDataSucceeded(state, action) {
      const { componentId, ...viResponse } = action?.payload ?? {};
      state.visualAnalysis.results = [viResponse, ...state.visualAnalysis.results];
      state.isDataExporting[componentId] = false;
      state.interpreterAgentClicked = false;
      state.openVisualAgentModal = true;
    },
    exportVisualDataFailed(state, action) {
      state.isDataExporting[action.payload.id] = false;
      state.openVisualAgentModal = true;
    },

    resetLLmScanData(state) {
      state.imageExport = [];
      state.errorData = [];
      state.exportProgress = {};
      state.isDataExporting = {};
      state.interpreterOptions = initialState.interpreterOptions;
    },
    setInterpreterOptions(state, action) {
      const { name, value } = action.payload;
      state.interpreterOptions[name] = value;
    },
    setTourSteps(state, action) {
      state.tourSteps = action?.payload;
    },
    startTour(state) {
      state.tourRun = true;
      // state.tourSteps = action.payload;
    },
    setTourTarget(state, action) {
      state.tourTarget = action?.payload;
    },
    setTourUrlTail(state, action) {
      state.tourUrlTail = action?.payload;
    },
    resetTour(state) {
      state.tourTarget = '';
      state.tourSteps = [];
      state.tourRun = false;
    },
    resetTourUrl(state) {
      state.tourUrlTail = '';
    },
    setIsHistoricalViOverlayActive(state, action) {
      state.isHistoricalViOverlayActive = action?.payload;
    },
    setOpenVisualAgentModal(state, action) {
      state.openVisualAgentModal = action?.payload;
    },
    setVisualAnalysis(state, action) {
      state.visualAnalysis.results = [...state.visualAnalysis.results, action?.payload];
    },
    setIsFirstTimeInterpreterAgentUsed(state, action) {
      state.isFirstTimeInterpreterAgentUsed = action.payload;
    },
    setInterpreterAgentClicked(state, action) {
      state.interpreterAgentClicked = action.payload;
    },
    submitFeedback(state) {
      state.loading = true;
    },
    submitFeedbackSucceeded(state) {
      state.loading = false;
    },
    submitFeedbackFailed(state) {
      state.loading = false;
    },
    setShareUrlTabList(state, action) {
      state.shareUrlTabList = action?.payload;
    },
    setTabList(state, action) {
      state.tabList = action?.payload;
    },
    setEncodedTabList(state, action) {
      state.encodedTabList = action?.payload;
    },
    getUserViResponseList(state) {
      state.viResponseListLoading = true;
      state.viResponseListError = false;
    },
    getUserViResponseListSucceeded(state, action) {
      state.viResponseListLoading = false;
      state.visualAnalysis = action?.payload;
    },
    getUserViResponseListFailed(state) {
      state.viResponseListLoading = false;
      state.viResponseListError = true;
    },
    cancelUserViResponseList(state) {
      state.viResponseListLoading = false;
    },
    getUserViResponseByShareId(state) {
      state.loading = true;
    },
    getUserViResponseByShareIdSucceeded(state, action) {
      state.loading = false;
      state.visualAnalysis.results = [action?.payload, ...state.visualAnalysis.results];
      state.openVisualAgentModal = true;
    },
    getUserViResponseByShareIdFailed(state) {
      state.loading = false;
      state.openVisualAgentModal = true;
    },
    updateViResponse(state) {
      state.loading = true;
    },
    updateViResponseSucceeded(state, action) {
      state.loading = false;
      state.visualAnalysis.results = state?.visualAnalysis?.results?.map((object) =>
        object?.id === action?.payload?.id ? { ...object, ...action.payload } : object
      );
    },
    updateViResponseFailed(state) {
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signInActions.signIn, (state) => ({
        ...state,
        ...initialState,
      }))
      .addCase(signInActions.signInSucceeded, (state, action) => ({
        ...state,
        isDemoUser: action?.payload?.user?.roles[0]?.name === DEMO_ROLE,
        userRole: action?.payload?.user?.roles[0]?.name,
      }))
      .addCase(signInActions.signInFailed, (state) => ({
        ...state,
        tokens: {},
        isDemoUser: false,
        userRole: null,
      }))
      // matched by type string to avoid importing users/slice (import cycle)
      .addCase(`${SLICE.USERS}/getLandingPageSucceeded`, (state, action) => ({
        ...state,
        isDemoModeActivated: action?.payload?.isDemoModeActivated,
      }))
      .addCase(`${SLICE.USERS}/updateLandingPageSucceeded`, (state, action) => ({
        ...state,
        isDemoModeActivated: action?.payload?.isDemoModeActivated,
      }));
  },
});
//
export const { actions: dashboardActions } = dashboardSlice;
